import React, { useState, useEffect } from "react";
import {
  Table,
  Card,
  Typography,
  Space,
  Row,
  Col,
  DatePicker,
  Select,
  Button,
  Statistic,
  Alert,
  Badge,
  Divider,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";
import RobinPage from "../components/RobinPage";
import {
  useGetAllPartnersSelectQuery,
  useGetAllPartnersBalanceLazyQuery,
} from "../graphql/generated/hooks";
import { usePermission } from "../hooks/usePermission";
import {
  SearchOutlined,
  ReloadOutlined,
  ShopOutlined,
  WalletOutlined,
} from "@ant-design/icons";

const { RangePicker } = DatePicker;
const { Text } = Typography;

// Types
interface StoreBalance {
  balance: string;
  storeId: number;
  storeName: string;
  storeNameAr: string;
}

interface PartnerBalance {
  partnerId: number;
  partnerName: string;
  partnerNameAr: string;
  totalBalance: string;
  storeBalances: StoreBalance[];
}

interface FilterState {
  dateFrom: string | null;
  dateTo: string | null;
  partnerId: number | null;
}

function PartnersBalance() {
  const { t, i18n } = useTranslation();
  const { data: partnersData, loading: partnersLoading } =
    useGetAllPartnersSelectQuery();

  // Use the generated lazy query hook
  const [fetchPartnersBalance, { data, loading, error }] =
    useGetAllPartnersBalanceLazyQuery({
      fetchPolicy: "network-only",
    });

  // Check permissions
  const permissions = usePermission([
    "getAllPartnersBalance",
    "getPartnersBalance",
    "viewPartnersBalance",
  ]);

  const hasPermission = Object.values(permissions).some(Boolean);

  // State management
  const [filters, setFilters] = useState<FilterState>({
    dateFrom: null,
    dateTo: null,
    partnerId: null,
  });

  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);

  // Execute the query with current filters
  const handleFetchPartnersBalance = () => {
    fetchPartnersBalance({
      variables: {
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        partnerId: filters.partnerId,
      },
    });
  };

  // Load data on component mount
  useEffect(() => {
    if (hasPermission) {
      handleFetchPartnersBalance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission]);

  // Filter handlers
  const handleFilterChange = (key: keyof FilterState, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      const dateFrom = dates[0].toISOString();
      const dateTo = dates[1].toISOString();
      setFilters((prev) => ({
        ...prev,
        dateFrom,
        dateTo,
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        dateFrom: null,
        dateTo: null,
      }));
    }
  };

  const resetFilters = () => {
    setFilters({
      dateFrom: null,
      dateTo: null,
      partnerId: null,
    });
  };

  // Format currency
  const formatCurrency = (amount: string) => {
    return `${parseFloat(amount).toFixed(2)} ${t("label.currency")}`;
  };

  // Store balance columns (for expanded row)
  const storeColumns: ColumnsType<StoreBalance> = [
    {
      title: t("label.store.id"),
      dataIndex: "storeId",
      key: "storeId",
      width: 100,
      render: (id: number) => (
        <Badge count={id} style={{ backgroundColor: "#52c41a" }} />
      ),
    },
    {
      title: t("label.store.name"),
      key: "storeName",
      render: (_, record) => {
        const currentLang = i18n.language;
        const name = currentLang === "ar" ? record.storeNameAr : record.storeName;
        return (
          <Space>
            <WalletOutlined style={{ color: "#52c41a" }} />
            <Text>{name}</Text>
          </Space>
        );
      },
    },
    {
      title: t("label.balance"),
      dataIndex: "balance",
      key: "balance",
      width: 180,
      align: "right",
      render: (balance: string) => {
        const amount = parseFloat(balance);
        return (
          <div
            style={{
              padding: "4px 12px",
              borderRadius: "4px",
              backgroundColor: amount >= 0 ? "#f6ffed" : "#fff2f0",
              border: `1px solid ${amount >= 0 ? "#b7eb8f" : "#ffccc7"}`,
              display: "inline-block",
            }}
          >
            <Text strong type={amount >= 0 ? "success" : "danger"}>
              {formatCurrency(balance)}
            </Text>
          </div>
        );
      },
    },
  ];

  // Partner balance columns
  const columns: ColumnsType<PartnerBalance> = [
    {
      title: t("label.partner.id"),
      dataIndex: "partnerId",
      key: "partnerId",
      width: 100,
      render: (id: number) => (
        <Badge count={id} style={{ backgroundColor: "#1890ff" }} />
      ),
    },
    {
      title: t("label.partner.name"),
      key: "partnerName",
      render: (_, record) => {
        const currentLang = i18n.language;
        const name = currentLang === "ar" ? record.partnerNameAr : record.partnerName;
        return (
          <Space>
            <ShopOutlined style={{ color: "#1890ff" }} />
            <Text strong>{name}</Text>
          </Space>
        );
      },
    },
    {
      title: t("label.total_balance"),
      dataIndex: "totalBalance",
      key: "totalBalance",
      width: 200,
      align: "right",
      render: (balance: string) => {
        const amount = parseFloat(balance);
        return (
          <div
            style={{
              padding: "4px 12px",
              borderRadius: "4px",
              backgroundColor: amount >= 0 ? "#f6ffed" : "#fff2f0",
              border: `1px solid ${amount >= 0 ? "#b7eb8f" : "#ffccc7"}`,
              display: "inline-block",
            }}
          >
            <Text strong type={amount >= 0 ? "success" : "danger"} style={{ fontSize: 16 }}>
              {formatCurrency(balance)}
            </Text>
          </div>
        );
      },
      sorter: (a, b) => parseFloat(a.totalBalance) - parseFloat(b.totalBalance),
    },
    {
      title: t("label.stores_count"),
      key: "storesCount",
      width: 120,
      align: "center",
      render: (_, record) => (
        <Badge
          count={record.storeBalances.length}
          style={{ backgroundColor: "#52c41a" }}
          showZero
        />
      ),
    },
  ];

  // Show permission error
  if (!hasPermission) {
    return (
      <RobinPage title={t("label.menu.partners_balance")}>
        <Alert
          message={t("error.access_denied")}
          description={t("error.insufficient_permissions")}
          type="error"
          showIcon
          style={{ margin: "20px 0" }}
        />
      </RobinPage>
    );
  }

  return (
    <RobinPage title={t("label.menu.partners_balance")}>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {/* Filters Card with gradient */}
        <Card
          title={
            <Space>
              <SearchOutlined style={{ color: "#1890ff" }} />
              <span>{t("label.filters")}</span>
            </Space>
          }
          style={{
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <Row gutter={[16, 16]}>
            {/* Date Range */}
            <Col xs={24} sm={12} md={8}>
              <RangePicker
                placeholder={[t("label.date_from"), t("label.date_to")]}
                style={{ width: "100%" }}
                value={
                  filters.dateFrom && filters.dateTo
                    ? [dayjs(filters.dateFrom), dayjs(filters.dateTo)]
                    : null
                }
                onChange={handleDateRangeChange}
              />
            </Col>

            {/* Partner Selection */}
            <Col xs={24} sm={12} md={8}>
              <Select
                placeholder={t("label.partner.select")}
                allowClear
                style={{ width: "100%" }}
                value={filters.partnerId}
                onChange={(value) => handleFilterChange("partnerId", value)}
                loading={partnersLoading}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={partnersData?.getAllPartners?.map((partner) => ({
                  label: `${partner.name} (${partner.nameAr})`,
                  value: partner.id,
                }))}
              />
            </Col>

            {/* Action Buttons */}
            <Col xs={24} sm={24} md={8}>
              <Space>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleFetchPartnersBalance}
                  loading={loading}
                  size="middle"
                >
                  {t("button.search")}
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={resetFilters}
                  size="middle"
                >
                  {t("button.reset_filters")}
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Statistics Cards */}
        {data?.getAllPartnersBalance && (
          <Row gutter={[16, 16]}>
            {/* Grand Total Card */}
            <Col xs={24} sm={12} md={8}>
              <Card
                style={{
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                }}
                styles={{ body: { padding: "24px" } }}
              >
                <Statistic
                  title={
                    <span style={{ color: "rgba(255,255,255,0.85)", fontSize: "14px" }}>
                      {t("label.grand_total")}
                    </span>
                  }
                  value={parseFloat(data.getAllPartnersBalance.grandTotal)}
                  precision={2}
                  suffix={<span style={{ color: "#fff", fontSize: "20px", marginLeft: "8px" }}>{t("label.currency")}</span>}
                  valueStyle={{
                    color: "#fff",
                    fontSize: 32,
                    fontWeight: "bold",
                  }}
                />
              </Card>
            </Col>

            {/* Total Partners Card */}
            <Col xs={24} sm={12} md={8}>
              <Card
                style={{
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                }}
                styles={{ body: { padding: "24px" } }}
              >
                <Statistic
                  title={
                    <span style={{ color: "rgba(255,255,255,0.85)", fontSize: "14px" }}>
                      {t("label.total_partners")}
                    </span>
                  }
                  value={data.getAllPartnersBalance.partners.length}
                  prefix={<ShopOutlined style={{ color: "#fff" }} />}
                  valueStyle={{
                    color: "#fff",
                    fontSize: 32,
                    fontWeight: "bold",
                  }}
                />
              </Card>
            </Col>

            {/* Total Stores Card */}
            <Col xs={24} sm={12} md={8}>
              <Card
                style={{
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                }}
                styles={{ body: { padding: "24px" } }}
              >
                <Statistic
                  title={
                    <span style={{ color: "rgba(255,255,255,0.85)", fontSize: "14px" }}>
                      {t("label.total_stores")}
                    </span>
                  }
                  value={data.getAllPartnersBalance.partners.reduce(
                    (sum, partner) => sum + partner.storeBalances.length,
                    0
                  )}
                  prefix={<WalletOutlined style={{ color: "#fff" }} />}
                  valueStyle={{
                    color: "#fff",
                    fontSize: 32,
                    fontWeight: "bold",
                  }}
                />
              </Card>
            </Col>
          </Row>
        )}

        {/* Partners Balance Table */}
        <Card
          title={
            <Space>
              <WalletOutlined style={{ color: "#1890ff" }} />
              <span>{t("label.partners_balance")}</span>
              {data?.getAllPartnersBalance && (
                <Badge
                  count={data.getAllPartnersBalance.partners.length}
                  style={{ backgroundColor: "#52c41a" }}
                />
              )}
            </Space>
          }
          style={{
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <Table
            columns={columns}
            dataSource={data?.getAllPartnersBalance?.partners || []}
            loading={loading}
            rowKey="partnerId"
            expandable={{
              expandedRowRender: (record) => (
                <div style={{ padding: "8px 0" }}>
                  <Divider orientation="left" style={{ margin: "8px 0 16px 0" }}>
                    <Space>
                      <ShopOutlined />
                      <Text strong>Store Balances</Text>
                    </Space>
                  </Divider>
                  <Table
                    columns={storeColumns}
                    dataSource={record.storeBalances}
                    pagination={false}
                    rowKey="storeId"
                    size="small"
                    style={{ backgroundColor: "#fafafa" }}
                  />
                </div>
              ),
              expandedRowKeys,
              onExpand: (expanded, record) => {
                if (expanded) {
                  setExpandedRowKeys([...expandedRowKeys, record.partnerId]);
                } else {
                  setExpandedRowKeys(
                    expandedRowKeys.filter((key) => key !== record.partnerId)
                  );
                }
              },
            }}
            pagination={{
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} ${t("label.of")} ${total} ${t(
                  "label.partners"
                )}`,
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
          />
        </Card>
      </Space>
    </RobinPage>
  );
}

export default PartnersBalance;

